/**
 * Table entity helpers (docs/plans/2d/2d-full-parity-plan.md
 * Faz 6.5): the real in-drawing TABLE object. Cells are a plain 2D string
 * matrix so the entity serializes through the ledger for free; geometry is
 * resolved on write (position + cell metrics) and rendered by EntityLayer.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';

export interface TableData {
    /** rows[ri][ci] cell text (header row first). */
    cells: string[][];
    /** insertion corner (world, Z-up XY). */
    position: [number, number, number];
    /** cell width/height in world units. */
    colWidth: number;
    rowHeight: number;
    /** first row drawn as a header band. */
    hasHeader: boolean;
    /** optional live data-link name backing these cells (DATALINK). */
    linkName?: string;
}

/** Parse one CSV/TSV block (RFC4180 quotes; tab when no comma present). */
export function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    const delim = text.includes('\t') && !text.includes(',') ? '\t' : ',';
    const pushField = () => { row.push(field); field = ''; };
    const pushRow = () => { pushField(); rows.push(row); row = []; };
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else inQuotes = false;
            } else field += ch;
            continue;
        }
        if (ch === '"') inQuotes = true;
        else if (ch === delim) pushField();
        else if (ch === '\n') pushRow();
        else if (ch === '\r') { /* swallow */ }
        else field += ch;
    }
    if (field.length > 0 || row.length > 0) pushRow();
    return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** Uniform column count (pad ragged rows). */
export function normalizeTableCells(cells: string[][]): string[][] {
    const width = cells.reduce((m, r) => Math.max(m, r.length), 0);
    return cells.map((r) => {
        const copy = [...r];
        while (copy.length < width) copy.push('');
        return copy;
    });
}

/**
 * Data links (DATALINK parity): named external datasets the app keeps
 * in-document; TABLE/REFRESH pull rows from here. Persisted per settings
 * slice (real binding, not a notify stub). Faz 8.7: xlsx links carry
 * sheet + range and re-read through the SheetJS helper (utils/xlsxReader);
 * picked files stay in a session-local handle map for Refresh re-reads.
 */
export interface DataLinkRecord {
    name: string;
    rows: string[][];
    source: string;
    /** 'csv' (parsed text) | 'xlsx' (SheetJS). Legacy records default csv. */
    kind?: 'csv' | 'xlsx';
    sheet?: string;
    range?: string;
    /** "Linked" once bound (OCS update_status); refresh errors keep it. */
    updateStatus?: string;
}
/** Session-local file cache for Refresh (handles never persist). */
const linkFileCache = new Map<string, File>();

interface DataLinkState {
    links: Record<string, DataLinkRecord>;
    /** Register/replace a named link's rows from CSV text. */
    set: (name: string, csvText: string, source: string) => void;
    /** Register an xlsx link (rows pre-read through utils/xlsxReader). */
    setXlsx: (name: string, rows: string[][], meta: { source: string; sheet: string; range?: string }) => void;
    /** Cache the picked File so Refresh can re-read it this session. */
    cacheFile: (name: string, file: File) => void;
    /** Re-read the source of a link; null when no cached handle. */
    refresh: (name: string) => Promise<number | null>;
    remove: (name: string) => void;
    get: (name: string) => DataLinkRecord | undefined;
    names: () => string[];
}

export const useDataLinkStore = create<DataLinkState>()(
    persist(
        (set, get) => ({
            links: {},
            set: (name, csvText, source) => set((s) => ({
                links: { ...s.links, [name]: { name, rows: parseCsv(csvText), source, kind: 'csv', updateStatus: 'Linked' } },
            })),
            setXlsx: (name, rows, meta) => set((s) => ({
                links: {
                    ...s.links,
                    [name]: { name, rows, source: meta.source, kind: 'xlsx', sheet: meta.sheet, range: meta.range, updateStatus: 'Linked' },
                },
            })),
            cacheFile: (name, file) => { linkFileCache.set(name, file); },
            refresh: async (name) => {
                const link = get().links[name];
                if (!link) return null;
                const file = linkFileCache.get(name);
                if (!file) return null;
                try {
                    if (link.kind === 'xlsx') {
                        const { workbookRows } = await import('../../utils/xlsxReader');
                        const rows = workbookRows(await file.arrayBuffer(), link.sheet ?? '', link.range);
                        if (rows.length === 0) return null;
                        set((s) => ({
                            links: { ...s.links, [name]: { ...link, rows, updateStatus: 'Linked' } },
                        }));
                        return rows.length;
                    }
                    const text = await file.text();
                    const rows = parseCsv(text);
                    if (rows.length === 0) return null;
                    set((s) => ({
                        links: { ...s.links, [name]: { ...link, rows, updateStatus: 'Linked' } },
                    }));
                    return rows.length;
                } catch {
                    return null;
                }
            },
            remove: (name) => {
                linkFileCache.delete(name);
                set((s) => {
                    const next = { ...s.links };
                    delete next[name];
                    return { links: next };
                });
            },
            get: (name) => get().links[name],
            names: () => Object.keys(get().links),
        }),
        { name: 'yap-datalinks', storage: settingsStorage, partialize: (s) => ({ links: s.links }) }
    )
);

// ---------------------------------------------------------------------------
// Cell formulas (Faz 8.6, OCS table.rs parity): a cell whose text starts
// with '=' evaluates at render time. Supported: direct cell refs (A1),
// ranges (A1:B10) inside SUM/AVERAGE/AVG/COUNT/MIN/MAX. Range walks clamp
// to the table bounds (OCS "avoid 1.8e19 iterations"); a cycle guard set
// protects against self-referencing formula chains (yap-specific: cells
// are plain strings, so A1==B1:A1 is expressible — OCS lacks this risk).
// ---------------------------------------------------------------------------

/** Column letters → 0-based index ("A"=0, "Z"=25, "AA"=26). */
function colIndex(letters: string): number {
    let n = 0;
    for (const ch of letters.toUpperCase()) {
        const v = ch.charCodeAt(0) - 65;
        if (v < 0 || v > 25) return -1;
        n = n * 26 + v;
    }
    return letters.length ? n : -1;
}

/** Column index → letters (inverse of colIndex). */
export function colLetters(index: number): string {
    let n = index + 1;
    let out = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}

/** Parse "A1" → { col, row } (0-based both). */
function parseCellRef(ref: string): { col: number; row: number } | null {
    const m = /^([A-Za-z]+)(\d+)$/.exec(ref.trim());
    if (!m) return null;
    return { col: colIndex(m[1]), row: parseInt(m[2], 10) - 1 };
}

/** Numeric value of a cell text (OCS parse::<f64> semantics: trimmed). */
function parseNumeric(text: string): number | null {
    const t = text.trim().replace(',', '.');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
}

/** Formula function names (AVERAGE accepts the AVG alias). */
const FORMULA_FUNCS = new Set(['SUM', 'AVERAGE', 'AVG', 'COUNT', 'MIN', 'MAX']);

/**
 * Evaluate one table cell to its DISPLAY value: formulas ("=…") resolve to
 * numbers; plain cells pass through unchanged. `visiting` carries the cell
 * chain currently being resolved — a re-entry is a formula cycle and
 * resolves to '#CYCLE!' (visible warning instead of a hang).
 */
export function evaluateTableCell(
    table: { cells?: string[][] },
    ri: number,
    ci: number,
    visiting: Set<string> = new Set(),
): string {
    const raw = table.cells?.[ri]?.[ci];
    const text = String(raw ?? '');
    if (!text.startsWith('=')) return text;
    const key = `${ri}:${ci}`;
    if (visiting.has(key)) return '#CYCLE!';
    visiting.add(key);
    try {
        const body = text.slice(1).replace(/\s+/g, '');
        if (!body) return '';
        // Function form: FUNC(range|ref[;...]).
        const fn = /^([A-Za-z]+)\((.+)\)$/.exec(body);
        if (fn) {
            const name = fn[1].toUpperCase();
            if (!FORMULA_FUNCS.has(name)) return '#NAME?';
            const values: number[] = [];
            let numericCells = 0;
            for (const arg of fn[2].split(/[,;]/)) {
                const span = /^([A-Za-z]+\d+):([A-Za-z]+\d+)$/.exec(arg);
                if (span) {
                    // Range: clamp both corners to the table bounds (OCS
                    // range clamp avoids astronomic iteration counts).
                    const a = parseCellRef(span[1]);
                    const b = parseCellRef(span[2]);
                    if (!a || !b) return '#REF!';
                    const r0 = Math.max(0, Math.min(a.row, b.row));
                    const r1 = Math.min((table.cells?.length ?? 1) - 1, Math.max(a.row, b.row));
                    const c0 = Math.max(0, Math.min(a.col, b.col));
                    const c1 = Math.min(Math.max(...(table.cells ?? [['']]).map((r) => r.length)) - 1, Math.max(a.col, b.col));
                    for (let r = r0; r <= r1; r++) {
                        for (let c = c0; c <= c1; c++) {
                            if (r === ri && c === ci) return '#CYCLE!';
                            const v = parseNumeric(evaluateTableCell(table, r, c, visiting));
                            if (v !== null) { values.push(v); numericCells++; }
                        }
                    }
                } else {
                    const ref = parseCellRef(arg);
                    if (!ref) return '#REF!';
                    if (ref.row === ri && ref.col === ci) return '#CYCLE!';
                    const v = parseNumeric(evaluateTableCell(table, ref.row, ref.col, visiting));
                    if (v !== null) { values.push(v); numericCells++; }
                }
            }
            switch (name) {
                case 'SUM': return trimNum(values.reduce((a, b) => a + b, 0));
                case 'AVERAGE':
                case 'AVG': return numericCells === 0 ? '#DIV/0!' : trimNum(values.reduce((a, b) => a + b, 0) / numericCells);
                case 'COUNT': return String(numericCells);
                case 'MIN': return values.length === 0 ? '0' : trimNum(Math.min(...values));
                case 'MAX': return values.length === 0 ? '0' : trimNum(Math.max(...values));
                default: return '#NAME?';
            }
        }
        // Direct cell reference form: "=A1".
        const ref = parseCellRef(body);
        if (ref) {
            const v = parseNumeric(evaluateTableCell(table, ref.row, ref.col, visiting));
            return v === null ? '#REF!' : trimNum(v);
        }
        return '#NAME?';
    } finally {
        visiting.delete(key);
    }
}

/** Format a formula result without float noise (12 significant digits). */
function trimNum(n: number): string {
    const rounded = Number(n.toPrecision(12));
    return String(rounded);
}

/** Display text of a cell — the single render/export entry point. */
export function tableCellDisplay(table: { cells?: string[][] }, ri: number, ci: number): string {
    return evaluateTableCell(table, ri, ci);
}
